import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Icon,
  useColorModeValue,
  Spinner,
  Center,
  Divider,
  Collapse,
  IconButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Flex,
} from '@chakra-ui/react';
import { 
  FiPhone, 
  FiMessageSquare, 
  FiChevronDown, 
  FiChevronUp, 
  FiFileText,
  FiPhoneIncoming,
  FiPhoneOutgoing,
  FiArrowDownLeft,
  FiArrowUpRight,
} from 'react-icons/fi';
import moment from 'moment';
import { supabase, isSupabaseConfigured } from '../../../utils/supabase';

/**
 * SMS Chat Bubble Component
 * Displays SMS messages in a conversation-style format
 */
function SMSChatBubble({ message, isOutbound }) {
  const outboundBg = useColorModeValue('blue.500', 'blue.600');
  const inboundBg = useColorModeValue('gray.100', 'gray.600');
  const outboundColor = 'white';
  const inboundColor = useColorModeValue('gray.800', 'white');
  
  return (
    <Flex 
      justify={isOutbound ? 'flex-end' : 'flex-start'} 
      w="100%"
      mb={2}
    >
      <Box
        maxW="80%"
        bg={isOutbound ? outboundBg : inboundBg}
        color={isOutbound ? outboundColor : inboundColor}
        px={4}
        py={2}
        borderRadius="lg"
        borderBottomRightRadius={isOutbound ? 'sm' : 'lg'}
        borderBottomLeftRadius={isOutbound ? 'lg' : 'sm'}
        position="relative"
      >
        <HStack spacing={1} mb={1} opacity={0.8}>
          <Icon 
            as={isOutbound ? FiArrowUpRight : FiArrowDownLeft} 
            boxSize={3} 
          />
          <Text fontSize="2xs" fontWeight="medium">
            {isOutbound ? 'Sent' : 'Received'}
          </Text>
        </HStack>
        <Text fontSize="sm" whiteSpace="pre-wrap">
          {message.content}
        </Text>
        <Text 
          fontSize="2xs" 
          opacity={0.7} 
          textAlign="right" 
          mt={1}
        >
          {message.createdAt 
            ? moment(message.createdAt).format('h:mm A')
            : ''}
        </Text>
      </Box>
    </Flex>
  );
}

/**
 * Call Card Component
 * Displays call information in an expandable card
 */
function CallCard({ call, isExpanded, onToggle }) {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBG = useColorModeValue('gray.50', 'gray.700');
  const transcriptBg = useColorModeValue('gray.50', 'gray.700');
  const isOutbound = call.direction === 'outbound';
  
  const formatDuration = (durationSec) => {
    if (!durationSec) return 'N/A';
    const minutes = Math.floor(durationSec / 60);
    const seconds = durationSec % 60;
    if (minutes === 0) return `${seconds}s`;
    return `${minutes}m ${seconds}s`;
  };
  
  return (
    <Box
      p={3}
      bg={bgColor}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      _hover={{ bg: hoverBG }}
      transition="all 0.2s"
      borderLeftWidth="4px"
      borderLeftColor={isOutbound ? 'blue.400' : 'purple.400'}
    >
      <HStack justify="space-between" align="flex-start">
        <HStack spacing={3} flex={1}>
          <Box
            p={2}
            bg={isOutbound ? 'blue.50' : 'purple.50'}
            borderRadius="full"
          >
            <Icon
              as={isOutbound ? FiPhoneOutgoing : FiPhoneIncoming}
              color={isOutbound ? 'blue.500' : 'purple.500'}
              boxSize={4}
            />
          </Box>
          <VStack align="flex-start" spacing={0}>
            <HStack spacing={2}>
              <Text fontSize="sm" fontWeight="semibold">
                {isOutbound ? 'Outbound Call' : 'Inbound Call'}
              </Text>
              <Badge 
                colorScheme={call.status === 'completed' ? 'green' : 'gray'} 
                fontSize="2xs"
              >
                {call.status}
              </Badge>
            </HStack>
            <HStack spacing={3} mt={1}>
              {call.duration && (
                <HStack spacing={1}>
                  <Text fontSize="xs" color="gray.500">Duration:</Text>
                  <Text fontSize="xs" fontWeight="medium" color="gray.700">
                    {formatDuration(call.duration)}
                  </Text>
                </HStack>
              )}
              <Text fontSize="xs" color="gray.400">
                {call.createdAt
                  ? moment(call.createdAt).format('MMM DD, h:mm A')
                  : 'Unknown time'}
              </Text>
            </HStack>
          </VStack>
        </HStack>

        {(call.transcript || call.recordingUrl || call.transcriptSummary) && (
          <IconButton
            icon={isExpanded ? <FiChevronUp /> : <FiChevronDown />}
            size="xs"
            variant="ghost"
            onClick={onToggle}
            aria-label="Toggle details"
          />
        )}
      </HStack>

      <Collapse in={isExpanded}>
        <Box mt={3} pt={3} borderTopWidth="1px" borderColor={borderColor}>
          {call.transcriptSummary && (
            <Box mb={call.transcript ? 3 : 0}>
              <HStack spacing={1} mb={2}>
                <Icon as={FiFileText} boxSize={3} color="cyan.600" />
                <Text fontSize="xs" fontWeight="semibold" color="cyan.700">
                  AI Summary
                </Text>
              </HStack>
              <Box
                p={3}
                bg={transcriptBg}
                borderRadius="md"
                borderLeftWidth="3px"
                borderLeftColor="cyan.400"
                fontSize="xs"
                whiteSpace="pre-wrap"
              >
                {call.transcriptSummary}
              </Box>
            </Box>
          )}

          {call.transcript && (
            <Box>
              <HStack spacing={1} mb={2}>
                <Icon as={FiFileText} boxSize={3} color="purple.500" />
                <Text fontSize="xs" fontWeight="semibold" color="purple.700">
                  Transcript
                </Text>
              </HStack>
              <Box
                maxH="200px"
                overflowY="auto"
                p={3}
                bg={transcriptBg}
                borderRadius="md"
                fontSize="xs"
                whiteSpace="pre-wrap"
              >
                {call.transcript}
              </Box>
            </Box>
          )}
          
          {call.recordingUrl && (
            <Box mt={3}>
              <Text fontSize="xs" fontWeight="semibold" mb={2}>Recording</Text>
              <audio controls style={{ width: '100%', height: '32px' }}>
                <source src={call.recordingUrl} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * Date Separator Component
 */
function DateSeparator({ date }) {
  const bgColor = useColorModeValue('gray.100', 'gray.700');
  
  return (
    <Flex align="center" my={4}>
      <Divider flex={1} />
      <Text 
        fontSize="xs" 
        color="gray.500" 
        px={3}
        py={1}
        bg={bgColor}
        borderRadius="full"
        fontWeight="medium"
      >
        {moment(date).format('MMMM D, YYYY')}
      </Text>
      <Divider flex={1} />
    </Flex>
  );
}

export default function CallHistoryPanel({ contactId, dealId }) {
  const [calls, setCalls] = useState([]);
  const [smsMessages, setSmsMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCalls, setExpandedCalls] = useState(new Set());

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const smsPanelBg = useColorModeValue('gray.50', 'gray.900');

  useEffect(() => {
    if (!contactId && !dealId) {
      setLoading(false);
      return;
    }

    if (!isSupabaseConfigured()) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        // Fetch calls
        let callsQuery = supabase
          .from('crm_dialpad_calls')
          .select('*')
          .order('started_at', { ascending: false })
          .limit(50);

        if (dealId) {
          callsQuery = callsQuery.eq('deal_id', dealId);
        } else if (contactId) {
          callsQuery = callsQuery.eq('contact_id', contactId);
        }

        const { data: callsData, error: callsError } = await callsQuery;

        // Fetch SMS
        let smsQuery = supabase
          .from('crm_dialpad_sms')
          .select('*')
          .order('sent_at', { ascending: true }) // Ascending for conversation view
          .limit(100);

        // Conversation is contact-scoped. deal_id-only would hide campaign
        // outbound (often null deal_id) and inbound replies attributed to a
        // different deal for the same person.
        if (contactId) {
          smsQuery = smsQuery.eq('contact_id', contactId);
        } else if (dealId) {
          smsQuery = smsQuery.eq('deal_id', dealId);
        }

        const { data: smsData, error: smsError } = await smsQuery;

        if (callsError) console.error('Error fetching calls:', callsError);
        if (smsError) console.error('Error fetching SMS:', smsError);

        // Transform calls
        const transformedCalls = (callsData || []).map(c => ({
          id: c.id,
          direction: c.direction,
          status: c.status,
          phoneNumber: c.direction === 'outbound' ? c.to_number : c.from_number,
          duration: c.duration,
          transcript: c.transcript,
          transcriptSummary: c.transcript_summary,
          recordingUrl: c.recording_url,
          createdAt: c.started_at,
          ...(c.metadata || {}),
        }));

        // Transform SMS
        const transformedSms = (smsData || []).map(s => ({
          id: s.id,
          direction: s.direction,
          status: s.status,
          phoneNumber: s.direction === 'outbound' ? s.to_number : s.from_number,
          content: s.content,
          createdAt: s.sent_at,
          ...(s.metadata || {}),
        }));

        setCalls(transformedCalls);
        setSmsMessages(transformedSms);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching Dialpad logs:', error);
        setLoading(false);
      }
    };

    fetchData();

    // Set up real-time subscription for calls
    const callsChannel = supabase
      .channel(`dialpad-calls-${contactId || dealId}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'crm_dialpad_calls',
        filter: dealId ? `deal_id=eq.${dealId}` : `contact_id=eq.${contactId}`
      }, () => {
        fetchData();
      })
      .subscribe();

    // Set up real-time subscription for SMS
    const smsChannel = supabase
      .channel(`dialpad-sms-${contactId || dealId}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'crm_dialpad_sms',
        filter: contactId ? `contact_id=eq.${contactId}` : `deal_id=eq.${dealId}`
      }, () => {
        fetchData();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(callsChannel);
      supabase.removeChannel(smsChannel);
    };
  }, [contactId, dealId]);

  // Group SMS messages by date
  const smsGroupedByDate = useMemo(() => {
    const groups = {};
    smsMessages.forEach(msg => {
      const date = msg.createdAt 
        ? moment(msg.createdAt).format('YYYY-MM-DD')
        : 'unknown';
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(msg);
    });
    return groups;
  }, [smsMessages]);

  const toggleCallExpand = (callId) => {
    setExpandedCalls(prev => {
      const newSet = new Set(prev);
      if (newSet.has(callId)) {
        newSet.delete(callId);
      } else {
        newSet.add(callId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <Center py={8}>
        <Spinner size="md" color="blue.500" />
      </Center>
    );
  }

  const hasData = calls.length > 0 || smsMessages.length > 0;

  if (!hasData) {
    return (
      <Box textAlign="center" py={8}>
        <Icon as={FiPhone} boxSize={12} color="gray.300" mb={3} />
        <Text color="gray.500" fontSize="sm">
          No Dialpad activity yet
        </Text>
        <Text fontSize="xs" color="gray.400">
          Calls and messages will appear here
        </Text>
      </Box>
    );
  }

  return (
    <Tabs colorScheme="blue" variant="soft-rounded" size="sm">
      <TabList mb={4}>
        <Tab>
          <HStack spacing={2}>
            <Icon as={FiMessageSquare} />
            <Text>Messages</Text>
            {smsMessages.length > 0 && (
              <Badge colorScheme="green" borderRadius="full">
                {smsMessages.length}
              </Badge>
            )}
          </HStack>
        </Tab>
        <Tab>
          <HStack spacing={2}>
            <Icon as={FiPhone} />
            <Text>Calls</Text>
            {calls.length > 0 && (
              <Badge colorScheme="blue" borderRadius="full">
                {calls.length}
              </Badge>
            )}
          </HStack>
        </Tab>
      </TabList>

      <TabPanels>
        {/* SMS Messages Tab */}
        <TabPanel px={0}>
          {smsMessages.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Icon as={FiMessageSquare} boxSize={10} color="gray.300" mb={3} />
              <Text color="gray.500" fontSize="sm">
                No text messages yet
              </Text>
            </Box>
          ) : (
            <Box
              bg={smsPanelBg}
              borderRadius="lg"
              p={4}
              maxH="500px"
              overflowY="auto"
            >
              {Object.entries(smsGroupedByDate)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([date, messages]) => (
                  <Box key={date}>
                    <DateSeparator date={date} />
                    {messages.map((msg) => (
                      <SMSChatBubble
                        key={msg.id}
                        message={msg}
                        isOutbound={msg.direction === 'outbound'}
                      />
                    ))}
                  </Box>
                ))}
            </Box>
          )}
        </TabPanel>

        {/* Calls Tab */}
        <TabPanel px={0}>
          {calls.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Icon as={FiPhone} boxSize={10} color="gray.300" mb={3} />
              <Text color="gray.500" fontSize="sm">
                No calls yet
              </Text>
            </Box>
          ) : (
            <VStack align="stretch" spacing={3}>
              {calls.map((call) => (
                <CallCard
                  key={call.id}
                  call={call}
                  isExpanded={expandedCalls.has(call.id)}
                  onToggle={() => toggleCallExpand(call.id)}
                />
              ))}
              {calls.length >= 50 && (
                <Text fontSize="xs" color="gray.500" textAlign="center" mt={2}>
                  Showing most recent 50 calls
                </Text>
              )}
            </VStack>
          )}
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
}

